import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { toUserMessage } from '@platform/api-client';

import { Alert, FormField } from '@/shared/ui';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import type { FoundLoginId } from '@/shared/types/auth.type';
import { PhoneVerifyField } from '../components/PhoneVerifyField';
import {
  findLoginIdByEmailSchema,
  findLoginIdByPhoneSchema,
} from '../schemas/auth.schemas';
import {
  useFindLoginIdByEmail,
  useFindLoginIdByPhone,
} from '../api/use-auth.mutation';

type Method = 'phone' | 'email';

/**
 * 아이디 찾기 View.
 *
 * 이메일 로그인을 없앤 뒤로 아이디를 잊으면 자력 복구가 완전히 막히므로 필수 화면이다.
 *
 * ── 두 방법의 노출 규칙이 다른 이유 ─────────────────────────────
 * - 휴대폰: OTP 로 번호 소유를 증명한 뒤라 결과를 **화면에 바로** 보여준다.
 * - 이메일: 소유 증명이 없다. 응답에 실으면 주소만으로 계정 존재를 캐는 창구가 되므로
 *   결과를 그 주소로 **발송**하고, 계정 유무와 무관하게 같은 메시지를 돌려준다.
 */
export function FindLoginIdView() {
  const [method, setMethod] = useState<Method>('phone');

  return (
    <div className="mx-auto w-full max-w-md py-10">
      <header className="mb-7">
        <h1 className="text-2xl font-bold -tracking-[0.02em] text-adobe-dark dark:text-neutral-100">
          아이디 찾기
        </h1>
        <p className="mt-1 break-keep text-sm text-adobe-muted dark:text-neutral-400">
          가입 시 등록한 휴대폰 또는 이메일로 아이디를 확인할 수 있습니다.
        </p>
      </header>

      <div
        role="tablist"
        className="mb-6 flex gap-1 rounded-xl bg-neutral-100 p-1 dark:bg-neutral-800"
      >
        {(
          [
            ['phone', '휴대폰으로 찾기'],
            ['email', '이메일로 찾기'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={method === value}
            onClick={() => setMethod(value)}
            className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-colors ${
              method === value
                ? 'bg-brand-500 text-white'
                : 'text-adobe-muted hover:text-adobe-dark dark:text-neutral-400 dark:hover:text-neutral-100'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {method === 'phone' ? <ByPhone /> : <ByEmail />}

      <div className="mt-6 flex items-center justify-between text-sm">
        <Link
          to={PATHS.REQUEST_PASSWORD_RESET}
          className="text-adobe-muted transition-colors hover:text-adobe-dark dark:text-neutral-400"
        >
          비밀번호 찾기
        </Link>
        <Link
          to={PATHS.LOGIN}
          className="text-adobe-muted transition-colors hover:text-adobe-dark dark:text-neutral-400"
        >
          로그인으로 돌아가기
        </Link>
      </div>
    </div>
  );
}

/** 휴대폰 OTP 를 통과한 뒤 계정 목록을 바로 보여준다. */
function ByPhone() {
  const [phone, setPhone] = useState('');
  const [verified, setVerified] = useState(false);
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [accounts, setAccounts] = useState<FoundLoginId[] | null>(null);

  const { mutate, isPending, error, reset } = useFindLoginIdByPhone();

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsed = findLoginIdByPhoneSchema.safeParse({ phone });
    if (!parsed.success) {
      setFieldError(parsed.error.flatten().fieldErrors.phone?.[0]);
      return;
    }
    setFieldError(undefined);
    mutate(parsed.data.phone, {
      onSuccess: (data) => setAccounts(data.accounts),
    });
  };

  if (accounts) return <AccountList accounts={accounts} />;

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      <PhoneVerifyField
        phone={phone}
        onPhoneChange={(value) => {
          setPhone(value);
          setVerified(false);
          if (fieldError) setFieldError(undefined);
          if (error) reset();
        }}
        verified={verified}
        onVerified={() => setVerified(true)}
      />
      {fieldError && <Alert tone="error">{fieldError}</Alert>}

      {error && <Alert tone="error">{toUserMessage(error)}</Alert>}

      <button
        type="submit"
        disabled={!verified || isPending}
        className="w-full rounded-lg bg-brand-500 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
      >
        {isPending ? '조회 중…' : '아이디 확인'}
      </button>
      {!verified && (
        <p className="text-xs text-adobe-muted dark:text-neutral-500">
          휴대폰 인증을 완료하면 아이디를 확인할 수 있습니다.
        </p>
      )}
    </form>
  );
}

/** 결과를 화면에 띄우지 않고 그 주소로 보낸다 — 계정 열거 방지. */
function ByEmail() {
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>();
  const { mutate, isPending, isSuccess, error, reset } =
    useFindLoginIdByEmail();

  if (isSuccess) {
    return (
      <div className="rounded-xl bg-neutral-50 p-6 text-center dark:bg-neutral-800/50">
        <p className="text-sm font-semibold text-adobe-dark dark:text-neutral-100">
          메일을 확인해주세요
        </p>
        <p className="mt-2 text-sm text-adobe-muted dark:text-neutral-400">
          입력하신 주소로 가입된 계정이 있다면 아이디를 발송했습니다.
        </p>
        <p className="mt-1 text-xs text-adobe-muted dark:text-neutral-500">
          메일이 오지 않으면 스팸함을 확인해주세요.
        </p>
      </div>
    );
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsed = findLoginIdByEmailSchema.safeParse({ email });
    if (!parsed.success) {
      setFieldError(parsed.error.flatten().fieldErrors.email?.[0]);
      return;
    }
    setFieldError(undefined);
    mutate(parsed.data.email);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      <FormField
        label="이메일"
        type="email"
        name="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (fieldError) setFieldError(undefined);
          if (error) reset();
        }}
        error={fieldError}
        hint="가입 시 사용한 이메일 주소로 아이디를 보내드립니다."
      />

      {error && <Alert tone="error">{toUserMessage(error)}</Alert>}

      <button
        type="submit"
        disabled={isPending}
        className="w-full rounded-lg bg-brand-500 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
      >
        {isPending ? '발송 중…' : '아이디 메일로 받기'}
      </button>
    </form>
  );
}

function AccountList({ accounts }: { accounts: FoundLoginId[] }) {
  if (accounts.length === 0) {
    return (
      <div className="space-y-3">
        <Alert tone="info">
          이 번호로 가입된 계정이 없습니다. 다른 번호로 가입하셨는지 확인해 주세요.
        </Alert>
        <Alert tone="warning">
          연락처를 인증하지 않으셨다면 {'<이메일로 찾기>'}를 이용해 주세요.
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ul className="divide-y divide-neutral-200 rounded-xl border border-neutral-200 dark:divide-neutral-700 dark:border-neutral-700">
        {accounts.map((account) => (
          <li
            key={account.loginId}
            className="flex items-center justify-between gap-3 px-4 py-3"
          >
            <span className="font-mono text-sm font-bold text-adobe-dark dark:text-neutral-100">
              {account.loginId}
            </span>
            <span className="text-xs text-adobe-muted dark:text-neutral-400">
              {account.orgSlug ? `@${account.orgSlug}` : '개인'}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-adobe-muted dark:text-neutral-500">
        기업 계정은 로그인할 때 아이디와 회사코드를 나눠 입력합니다. 예를 들어{' '}
        <b>odin@howcost</b> 는 아이디 <b>odin</b>, 회사코드 <b>howcost</b> 입니다.
      </p>
      <Link
        to={PATHS.LOGIN}
        className="block w-full rounded-lg bg-brand-500 py-2.5 text-center text-sm font-bold text-white transition-colors hover:bg-brand-600"
      >
        로그인하러 가기
      </Link>
    </div>
  );
}
